import React, { useCallback, useMemo, useState } from "react";
import type { AgGrid } from "@jsq/ds/core";
import { useUrlFilters } from "../hooks/useUrlFilters";
import { WorkflowDetail } from "./Detail";
import { useWorkflowFilterOptions } from "./hooks/useWorkflowFilterOptions";
import { WorkflowTable } from "./WorkflowTable";
import type { WorkflowInstanceRow } from "./types";

export type WorkflowFilterMode = "assigned_to_me" | "all_open";

type WorkflowEngineProps = {
  filterMode: WorkflowFilterMode;
  onHasActiveFiltersChange?: (has: boolean) => void;
  clearFiltersTrigger?: number;
  dashboardFilterModel?: AgGrid.FilterModel | null;
};

export const WorkflowEngine: React.FC<WorkflowEngineProps> = ({
  filterMode,
  onHasActiveFiltersChange,
  clearFiltersTrigger,
  dashboardFilterModel,
}) => {
  const { urlFilterModel, hasUrlFilters, updateUrlFilters } = useUrlFilters();

  const effectiveFilterModel = useMemo<AgGrid.FilterModel | null>(() => {
    if (!dashboardFilterModel && !urlFilterModel) return null;
    return { ...(dashboardFilterModel ?? {}), ...(urlFilterModel ?? {}) };
  }, [dashboardFilterModel, urlFilterModel]);

  const hasEffectiveFilters =
    hasUrlFilters ||
    (dashboardFilterModel !== null &&
      dashboardFilterModel !== undefined &&
      Object.keys(dashboardFilterModel).length > 0);
  const { filterOptions, loading: filterOptionsLoading } = useWorkflowFilterOptions();

  // Resolved from the first controlTowerWorkflows response — piggybacked on the
  // list query so no extra round-trip is needed. Empty string until resolved;
  // assignee gating in ActionBar/TaskDetail is permissive when empty (defers to BE).
  const [workflowUserId, setWorkflowUserId] = useState("");

  // Drawer state — instanceRow comes from the datasource cache, no local array needed
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowInstanceRow | null>(null);
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);

  const handleWorkflowClick = (_workflowId: string, instanceRow?: WorkflowInstanceRow) => {
    setSelectedWorkflow(instanceRow ?? null);
    setActiveTaskId(null);
  };

  const handleTaskClick = (
    _workflowId: string,
    taskId: string,
    instanceRow?: WorkflowInstanceRow,
  ) => {
    setSelectedWorkflow(instanceRow ?? null);
    setActiveTaskId(taskId);
  };

  const handleFilterChanged = useCallback(
    (filterModel: AgGrid.FilterModel | null) => {
      if (!filterModel) {
        updateUrlFilters(null);
        return;
      }
      const dashboardKeys = dashboardFilterModel ? Object.keys(dashboardFilterModel) : [];
      const urlModel = Object.fromEntries(
        Object.entries(filterModel).filter(([key]) => !dashboardKeys.includes(key)),
      );
      updateUrlFilters(Object.keys(urlModel).length > 0 ? urlModel : null);
    },
    [updateUrlFilters, dashboardFilterModel],
  );

  const handleClose = useCallback(() => {
    setSelectedWorkflow(null);
    setActiveTaskId(null);
  }, []);

  return (
    <>
      <WorkflowTable
        showExpanded={filterMode === "all_open"}
        filterMode={filterMode}
        filterOptions={filterOptions}
        filterOptionsLoading={filterOptionsLoading}
        initialFilterModel={effectiveFilterModel}
        hasInitialUrlFilters={hasEffectiveFilters}
        onHasActiveFiltersChange={onHasActiveFiltersChange}
        clearFiltersTrigger={clearFiltersTrigger}
        onFilterChanged={handleFilterChanged}
        onWorkflowClick={handleWorkflowClick}
        onTaskClick={handleTaskClick}
        onViewerWorkflowUserIdResolved={setWorkflowUserId}
      />
      <WorkflowDetail
        workflow={selectedWorkflow}
        activeTaskId={activeTaskId}
        onClose={handleClose}
        onTaskIdChange={setActiveTaskId}
        workflowUserId={workflowUserId}
      />
    </>
  );
};

export default WorkflowEngine;

export { WorkflowDetail } from "./Detail";
export { TabType as WorkflowDetailDrawerTabType } from "./types";
export type {
  CommentAuthor,
  CommentBody,
  PostCommentInput,
  WorkflowComment,
  WorkflowDetailDrawerProps,
} from "./types";
